import axios from 'utils/axios'

import { PaymentsParams, PaymentsResponse } from './types'

// BE StripePaymentIntentOut (POST /payments/stripe/intent), envelope unwrapped by
// the axios interceptor so we read these fields directly.
interface StripeIntentResponse {
  id: number | string
  client_secret: string
  status?: string
  amount_nzd?: number | string
  payment_amount_nzd?: number | string
  fee_nzd?: number | string
}

export async function payments(
  params: PaymentsParams,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  recaptchaToken?: string
): Promise<PaymentsResponse> {
  // The old `/payments` endpoint does NOT exist on the serverless backend.
  // Create a Stripe PaymentIntent via POST /payments/stripe/intent. The BE
  // returns a FLAT object { id, client_secret, status, amount_nzd,
  // payment_amount_nzd, fee_nzd } (no nested payment_intent / review_data),
  // so map it back onto the PaymentsResponse shape the components consume.
  const response = await axios.post<StripeIntentResponse>(
    '/payments/stripe/intent',
    {
      amount: params.amount,
      payment_type: params.payment_type,
      from_name: params.fromName,
      to_name: params.toName,
      from_email: params.fromEmail,
      to_email: params.toEmail,
      note: params.note ?? '',
      // Link the intent to the checkout order it funds: the quote then
      // survives card entry (the 5-min TTL routinely dies while typing card
      // details) and settlement executes the purchase server-side [BUG-016].
      ...(params.review_order_id != null
        ? { review_order_id: params.review_order_id }
        : {})
    }
  )

  const data = response.data

  return {
    status: 'ok',
    success: true,
    review_data: {
      amount: String(data.amount_nzd ?? params.amount),
      fee: String(data.fee_nzd ?? '0'),
      from_email: params.fromEmail ?? '',
      from_name: params.fromName ?? '',
      note: params.note ?? '',
      payment_amount: String(data.payment_amount_nzd ?? params.amount),
      payment_type: params.payment_type,
      toEmail: params.toEmail ?? '',
      toName: params.toName ?? '',
      // Pure top-ups draw nothing from the existing wallet balance; the BE no
      // longer returns this field, so default to 0.
      wallet_use_amount: '0',
      voucher_amount: params.voucher_amount ?? '0'
    },
    payment_intent: {
      id: String(data.id),
      client_secret: data.client_secret,
      metadata: {
        amount: String(data.amount_nzd ?? params.amount),
        fee: String(data.fee_nzd ?? '0'),
        from_email: params.fromEmail ?? '',
        from_name: params.fromName ?? '',
        note: params.note,
        to_email: params.toEmail ?? '',
        to_name: params.toName ?? ''
      }
    }
  }
}
